import React, { Suspense, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import SingleSelect from "../../../Components/SingleSelect";
import MultiSelect from "../../../Components/MultiSelect";

const AgentNoMemo = React.lazy(() => import("../Types/Agent"));
const AgentMultipleNoMemo = React.lazy(() => import("../Types/AgentMultiple"));

const AgentPicker = ({
  data,
  idField,
  cvType,              // CV type string e.g. "manufacturer", "authority"
  showSelector = false,
  onUpdate,
  onHide,
  isUpdating = false,
}) => {
  const isMulti = Array.isArray(data);

  // Derive the initial fieldValue(s) from data — these are the agent KU IDs
  const getFieldValue = () => {
    if (isMulti) return (data || []).map((a) => a?.id).filter(Boolean);
    return data?.id ?? null;
  };

  // Tracks what the user has staged in the dropdown.
  // Uses a sentinel so we can distinguish "unchanged" from null/empty selection.
  const UNCHANGED = useRef(Symbol("UNCHANGED"));
  const [localSelection, setLocalSelection] = useState(UNCHANGED.current);

  const hasChanges = localSelection !== UNCHANGED.current;

  const handleSingleChange = (_cvType, _id, selected) => {
    // selected is { value, label } or null
    setLocalSelection(selected);
  };

  const handleMultiChange = (_cvType, _id, selected) => {
    // selected is [{ value, label }, ...]
    setLocalSelection(selected);
  };

  const handleUpdate = async () => {
    const isEmpty = isMulti
      ? !localSelection || localSelection.length === 0
      : !localSelection;

    if (isEmpty) {
      alert("Please select a value before updating.");
      return;
    }

    if (onUpdate && hasChanges) {
      const success = await onUpdate(localSelection, isMulti);
      setLocalSelection(UNCHANGED.current);
      if (success && onHide) {
        onHide();
      }
    }
  };

  return (
    <>
      {showSelector && (
        <div className="d-flex align-items-center gap-2 mb-2">
          <div style={{ minWidth: "260px", flex: 1 }}>
            {isMulti ? (
              <MultiSelect
                fieldId={idField}
                fieldName="agent"
                fieldType="agent"
                cvType={cvType}
                language="EN"
                handleMultiSelectFieldChange={handleMultiChange}
                fieldValue={getFieldValue()}
                enableEdit={!isUpdating}
              />
            ) : (
              <SingleSelect
                fieldId={idField}
                fieldName="agent"
                fieldType="agent"
                cvType={cvType}
                language="EN"
                handleSingleSelectFieldChange={handleSingleChange}
                fieldValue={getFieldValue()}
                enableEdit={!isUpdating}
              />
            )}
          </div>

          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={handleUpdate}
            disabled={!hasChanges || isUpdating}
          >
            Update
          </button>

          {isUpdating && <Spinner animation="border" size="sm" />}
        </div>
      )}

      {data && isMulti ? (
        <Suspense fallback={<div>Loading ...</div>}>
          <AgentMultipleNoMemo data={data} idField={idField} />
        </Suspense>
      ) : (
        <Suspense fallback={<div>Loading ...</div>}>
          <AgentNoMemo data={data} idField={idField} />
        </Suspense>
      )}
    </>
  );
};

export default AgentPicker;
